import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Image as ImageIcon, Sparkles, Download, RefreshCw } from 'lucide-react';

const AIImageWidget = ({ onImageGenerated }) => {
    const [prompt, setPrompt] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const generateImage = () => {
        if (!prompt.trim()) {
            setError('Please enter a prompt');
            return;
        }
        setError('');
        setLoading(true);
        // Add random seed to bypass cache if generating again with same prompt
        const seed = Math.floor(Math.random() * 10000);
        const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?n=${seed}`;
        setImageUrl(url);

        if (onImageGenerated) {
            onImageGenerated(url);
        }
    };

    const handleImageLoad = () => {
        setLoading(false);
    };

    const handleImageError = () => {
        setLoading(false);
        setError('Failed to load image. Please try again.');
    };

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-indigo-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                    <Sparkles className="text-purple-600" />
                    AI Image Generator
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Turn your text into amazing images using AI.
                </p>
            </div>

            <div className="p-6 space-y-6">
                {/* Input Section */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700">
                        Describe your image
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="e.g., A futuristic city with flying cars, cyberpunk style"
                            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                            onKeyDown={(e) => e.key === 'Enter' && generateImage()}
                        />
                        <button
                            onClick={generateImage}
                            disabled={loading || !prompt.trim()}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? <RefreshCw className="animate-spin" size={20} /> : <Sparkles size={20} />}
                            Generate
                        </button>
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                </div>

                {/* Image Display Section */}
                <div className="relative min-h-[300px] bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden group">
                    {!imageUrl && !loading && (
                        <div className="text-center p-8 text-slate-400">
                            <ImageIcon size={48} className="mx-auto mb-3 opacity-50" />
                            <p>Enter a prompt and click Generate to see magic happen!</p>
                        </div>
                    )}

                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                            <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-3"></div>
                            <p className="text-purple-600 font-medium animate-pulse">Creating masterpiece...</p>
                        </div>
                    )}

                    {imageUrl && (
                        <>
                            <img
                                src={imageUrl}
                                alt="Generated AI"
                                className={`w-full h-full object-contain max-h-[500px] transition-opacity duration-500 ${loading ? 'opacity-50' : 'opacity-100'}`}
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                            />
                            {/* Overlay Actions */}
                            {!loading && (
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <a
                                        href={imageUrl}
                                        download={`ai-generated-${Date.now()}.jpg`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-2 bg-white/90 hover:bg-white text-slate-700 rounded-lg shadow-lg backdrop-blur-sm transition-all flex items-center gap-2 text-sm font-medium"
                                    >
                                        <Download size={16} />
                                        Download
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

AIImageWidget.propTypes = {
    onImageGenerated: PropTypes.func,
};

export default AIImageWidget;
